import {
  Advertiser,
  AdvertiserService,
  Asset,
  AssetService,
  AssetType,
  Document,
  DocumentService,
  Location,
  LocationService,
  Project,
  ProjectService,
  ScriptService,
  SecretService,
  TAdvertiserId,
  TCreateLocationRequest,
  TCreateProjectRequest,
  TUpdateAdvertiserRequest,
  TUpdateLocationRequest,
  TUpdateProjectRequest,
} from '@talon/web/lib/api';
import { StorageProperties } from '@talon/web/src/types/storageTypes';
import Storage from '@talon/web/src/utils/storage';
import { kebabCase } from 'lodash';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { BaseStore } from './baseStore'; // for some dumb reason, these have to be imported individually
import { RootStore } from './rootStore'; // for some dumb reason, these have to be imported individually

export class ProjectStore extends BaseStore {
  isLoadingList = true;
  isSaving = false;
  // True once fetchProjectList() has completed at least one full attempt, and never reset to
  // false again afterward — unlike isLoadingList, which legitimately flips back to true on any
  // later background refresh. UI that gates "children vs. loading spinner" (ProjectRoute, Home)
  // must key off this instead of isLoadingList: gating on isLoadingList unmounted the page on
  // every refresh, which unmounted ProjectSelector, which re-triggered fetchProjectList() on
  // remount, which flipped isLoadingList again — a self-sustaining infinite loop.
  hasLoadedList = false;
  // Tracks an in-flight fetchProjectList() call so concurrent callers (Home's mount effect,
  // ProjectSelector's mount effect, and any remount of either during the post-login render
  // churn) share one request instead of each firing their own — this list carries a heavy
  // relation set and was observed firing 3x in a row on a single login.
  private _fetchProjectListPromise: Promise<void> | null = null;

  currentProject: Project | undefined = undefined;
  projectList: Project[] | undefined = [];

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      isLoadingList: observable,
      hasLoadedList: observable,
      isSaving: observable,
      currentProject: observable,
      projectList: observable,
      setCurrentProjectId: action,
      setCurrentProjectName: action,
      openProjectByName: action,
      clientList: computed,
    });
  }

  setIsSaving(isSaving: boolean) {
    runInAction(() => (this.isSaving = isSaving));
  }

  /**     PROJECTS      */

  fetchProjectList = async (): Promise<void> => {
    // Join an already-in-flight request rather than firing a redundant duplicate.
    if (this._fetchProjectListPromise) return this._fetchProjectListPromise;

    const run = async () => {
      try {
        if (!this.isLoadingList) runInAction(() => (this.isLoadingList = true));

        // TODO implement pagination
        // Deliberately lighter than a single project's full detail relations (no
        // documents/scripts/assets) - this list backs the home page cards and project
        // switcher, which don't use those. The currently-opened project gets enriched
        // with the full relation set separately, see enrichCurrentProject below.
        const { projects } = await this.q(
          ProjectService.listProjects(undefined, undefined, undefined, 'client,roles,contracts,estimates,sessions')
        );
        runInAction(() => {
          this.projectList = projects;
          const storedProjectId = Storage.get(StorageProperties.CurrentProjectId);
          const targetProjectId = storedProjectId || (projects.length > 0 ? projects[0].id : undefined);
          // fetchProjectList is called independently by multiple mounted components (e.g. the
          // Home page and the nav's ProjectSelector) - without this guard, each call would
          // redundantly re-run setCurrentProjectId's full project enrichment fetch even though
          // the current project hasn't actually changed.
          if (targetProjectId && targetProjectId !== this.currentProject?.id) {
            this.setCurrentProjectId(targetProjectId);
          }
        });
      } catch (e) {
        this.handleError(e);
      } finally {
        runInAction(() => {
          this.isLoadingList = false;
          this.hasLoadedList = true;
        });
        this._fetchProjectListPromise = null;
      }
    };

    this._fetchProjectListPromise = run();
    return this._fetchProjectListPromise;
  };

  createProject = async ({
    name,
    groupId,
    clientId,
    product,
    jobCode,
    talentBudgetInPennies,
    is_union,
  }: TCreateProjectRequest) => {
    try {
      this.setIsSaving(true);
      const { project } = await this.q(
        ProjectService.createProject({ name, groupId, clientId, product, jobCode, talentBudgetInPennies, is_union })
      );
      if (project) {
        // Await the refreshed list: callers (e.g. the Project Wizard) immediately create child
        // records via stores that look the new project up in projectList.
        await this.fetchProjectList();
        runInAction(() => {
          this.currentProject = project;
        });
      }
      return project;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  findProjectByName = (projectName: string) =>
    this.projectList?.find((project) => kebabCase(project.name) === kebabCase(projectName));

  setCurrentProjectId = (projectId: string) => {
    this.currentProject = this.projectList?.find((project) => project.id === projectId);
    Storage.set(StorageProperties.CurrentProjectId, projectId);
    this.enrichCurrentProject(projectId);
    return this.currentProject;
  };

  /**
   * Makes the project a URL names (/project/<name>) the current one. Pages render currentProject,
   * so without this a shared link or bookmark showed whichever project was last selected instead.
   * Returns false when no project has that name.
   */
  openProjectByName = (projectName: string): boolean => {
    const project = this.findProjectByName(projectName);
    if (!project) return false;
    if (project.id !== this.currentProject?.id) this.setCurrentProjectId(project.id);
    return true;
  };

  setCurrentProjectName = (projectName: string) => {
    this.currentProject = this.findProjectByName(projectName);
    if (!this.currentProject) {
      console.error('Project with name "%s" not found', projectName);
      return;
    }
    Storage.set(StorageProperties.CurrentProjectId, this.currentProject.id);
    this.enrichCurrentProject(this.currentProject.id);
  };

  /**
   * The project list is fetched with a light relation set (see fetchProjectList).
   * Once a project is actually opened, fetch it again with the full relation set
   * every project-scoped page needs (documents, scripts, assets, etc.) and merge
   * the result into both currentProject and projectList.
   */
  enrichCurrentProject = async (projectId: string) => {
    try {
      const { project } = await this.q(
        ProjectService.getProject(projectId, 'documents,estimates,roles,contracts,sessions,scripts,client,assets')
      );
      if (!project) return;

      runInAction(() => {
        this.projectList = this.projectList?.map((p) => (p.id === project.id ? project : p));
        // Only overwrite currentProject if the user hasn't since switched to a
        // different project while this fetch was in flight
        if (this.currentProject?.id === project.id) this.currentProject = project;
      });
    } catch (e) {
      this.handleError(e);
    }
  };

  deleteProject = async (projectId: string) => {
    try {
      this.setIsSaving(true);
      await this.q(ProjectService.deleteProject(projectId));
      runInAction(() => {
        this.projectList = this.projectList?.filter((p) => p.id !== projectId);
      });
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  updateProject = async (projectId: string, req: TUpdateProjectRequest) => {
    try {
      this.setIsSaving(true);
      const { project } = await this.q(ProjectService.updateProject(projectId, req));
      if (!project) throw new Error('No project returned from update');
      // retrieve documents and estimates
      const { project: fullProject } = await this.q(
        ProjectService.getProject(projectId, 'documents,estimates,sessions,locations,client,contracts,roles,scripts')
      );
      if (!fullProject) throw new Error('Could not retrieve updated project details');
      runInAction(() => {
        this.projectList = this.projectList?.map((p) => (p.id === projectId ? fullProject : p));
        if (this.currentProject?.id === projectId) {
          this.currentProject = fullProject;
        }
      });
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  /**         SCRIPTS       */

  createScript = async (projectId: string, name: string) => {
    try {
      this.setIsSaving(true);
      const { script } = await this.q(ScriptService.createScript({ name, projectId }));
      return script;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  getScriptsByProject = async (projectId: string) => {
    try {
      const { scripts } = await this.q(ScriptService.listScripts(projectId));
      return scripts;
    } catch (e) {
      this.handleError(e);
    }
  };

  getScriptByDocId = async (documentId: string) => {
    try {
      const { script } = await this.q(ScriptService.getScriptByDocId(documentId));
      return script;
    } catch (e) {
      this.handleError(e);
    }
  };

  /**         DOCUMENTS     */

  createDocument = async (file: File, documentType: string, useAi: string) => {
    if (!this.currentProject) {
      throw new Error('No current project');
    }

    try {
      this.setIsSaving(true);
      const { document } = await this.q(
        DocumentService.createDocument({
          file,
          name: file.name,
          type: documentType,
          project_id: this.currentProject.id,
          useAi: useAi,
        })
      );
      if (document) {
        runInAction(() => {
          this.currentProject?.documents?.push(document);
        });
      }
      return document;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  getSignedDownloadLink = async (documentId: string) => {
    try {
      const url = await this.q(DocumentService.getSignedUrl(documentId));
      return url;
    } catch (e) {
      this.handleError(e);
    }
  };

  updateDocument = async (documentId: string, updatedDocument: Document) => {
    const request = {
      ...updatedDocument,
      project_id: this.currentProject?.id,
    };
    try {
      this.setIsSaving(true);
      const { document } = await this.q(DocumentService.updateDocument(documentId, request));
      return document;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  deleteDocument = async (documentId: string) => {
    try {
      this.setIsSaving(true);
      await this.q(DocumentService.deleteDocument(documentId));
      runInAction(() => {
        if (this.currentProject) {
          this.currentProject.documents = this.currentProject?.documents?.filter((d) => d.id !== documentId);
        }
      });
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  /**         ASSETS        */

  createAsset = async (file: File, assetType: string, description?: string) => {
    if (!this.currentProject) {
      throw new Error('No current project');
    }

    try {
      this.setIsSaving(true);
      const { asset } = await this.q(
        AssetService.createAsset({
          file,
          name: file.name,
          type: assetType,
          project_id: this.currentProject.id,
          description: description || '',
        })
      );
      if (asset) {
        runInAction(() => {
          if (!this.currentProject) return;
          if (!this.currentProject.assets) {
            this.currentProject.assets = [];
          }
          // Create new array reference so React detects the change
          this.currentProject.assets = [...this.currentProject.assets, asset];
        });
      }
      return asset;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  createAssetFromDrive = async (driveFileId: string, fileName: string, assetType: AssetType, description?: string) => {
    if (!this.currentProject) {
      throw new Error('No current project');
    }

    try {
      this.setIsSaving(true);
      const { asset } = await this.q(
        AssetService.createAssetFromDrive({
          drive_file_id: driveFileId,
          name: fileName,
          type: assetType,
          project_id: this.currentProject.id,
          description: description || '',
        })
      );
      if (asset) {
        runInAction(() => {
          if (!this.currentProject) return;
          if (!this.currentProject.assets) {
            this.currentProject.assets = [];
          }
          // Create new array reference so React detects the change
          this.currentProject.assets = [...this.currentProject.assets, asset];
        });
      }
      return asset;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  createAssetFromCloudStorage = async (
    provider: 'google',
    fileId: string,
    fileName: string,
    assetType: AssetType,
    description?: string
  ) => {
    if (!this.currentProject) {
      throw new Error('No current project');
    }

    try {
      this.setIsSaving(true);
      const { asset } = await this.q(
        AssetService.createAssetFromCloud({
          provider,
          file_id: fileId,
          name: fileName,
          type: assetType,
          project_id: this.currentProject.id,
          description: description || '',
        })
      );
      if (asset) {
        runInAction(() => {
          if (!this.currentProject) return;
          if (!this.currentProject.assets) {
            this.currentProject.assets = [];
          }
          // Create new array reference so React detects the change
          this.currentProject.assets = [...this.currentProject.assets, asset];
        });
      }
      return asset;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  getAsset = async (assetId: string, relations?: string) => {
    try {
      const { asset } = await this.q(AssetService.getAsset(assetId, relations));
      return asset ?? undefined;
    } catch (e) {
      this.handleError(e);
    }
  };

  getSignedAssetUrl = async (assetId: string) => {
    try {
      const url = await this.q(AssetService.getSignedUrl(assetId));
      return url;
    } catch (e) {
      this.handleError(e);
    }
  };

  getAssetThumbnailUrl = async (assetId: string) => {
    try {
      const url = await this.q(AssetService.getThumbnailUrl(assetId));
      return url;
    } catch (e) {
      this.handleError(e);
    }
  };

  updateAsset = async (assetId: string, updatedAsset: Asset) => {
    const request = {
      name: updatedAsset.name,
      description: updatedAsset.description,
      processing_status: updatedAsset.processing_status,
    };
    try {
      this.setIsSaving(true);
      const { asset } = await this.q(AssetService.updateAsset(assetId, request));
      if (!asset) throw new Error('No asset returned from update');
      const newAssets = this.currentProject?.assets?.map((a) => (a.id === asset.id ? asset : a));
      runInAction(() => {
        if (this.currentProject) {
          this.currentProject.assets = newAssets || [];
        }
      });
      return asset;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  deleteAsset = async (assetId: string) => {
    try {
      this.setIsSaving(true);
      await this.q(AssetService.deleteAsset(assetId));
      runInAction(() => {
        if (this.currentProject) {
          this.currentProject.assets = this.currentProject?.assets?.filter((a) => a.id !== assetId);
        }
      });
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  getStorageUsage = async (groupId: string) => {
    try {
      const usage = await this.q(AssetService.getStorageUsage(groupId));
      return usage;
    } catch (e) {
      this.handleError(e);
    }
  };

  get clientList() {
    if (!this.projectList) return [];
    return this.projectList.reduce((list: Advertiser[], p: Project) => {
      if (!p.client) return list;
      return list.find((c) => c.id === p.client.id) ? list : list.concat(p.client);
    }, []);
  }

  createClient = async (clientData: Partial<Advertiser>) => {
    try {
      this.setIsSaving(true);
      if (!clientData.name) {
        throw new Error('Client name is required');
      }
      const request = {
        name: clientData.name,
        isSagSignatory: clientData.isSagSignatory || false,
        isJpcAuthorizer: clientData.isJpcAuthorizer || false,
      };
      // create the client
      const { advertiser } = await this.q(AdvertiserService.createAdvertiser(request));
      if (advertiser) {
        runInAction(() => {
          // add to client list in project store
          if (this.projectList && !this.clientList.find((c) => c.id === advertiser.id)) {
            this.projectList = this.projectList.map((p) => (p.client?.id === 'NEW' ? { ...p, client: advertiser } : p));
          }
        });
      }
      return advertiser;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  updateClient = async (clientId: TAdvertiserId, updatedClient: TUpdateAdvertiserRequest) => {
    try {
      this.setIsSaving(true);
      const { advertiser } = await this.q(AdvertiserService.updateAdvertiser(clientId, updatedClient));
      if (!advertiser) throw new Error('No Client returned from update');
      runInAction(() => {
        if (this.currentProject) {
          this.currentProject.client = { ...this.currentProject.client, ...advertiser };
        }
      });
      return advertiser;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  getMapApiKey = async () => {
    try {
      const { key } = await this.q(SecretService.getSecretKey({ key: 'mapApiKey' }));
      return key;
    } catch (e) {
      this.handleError(e);
    }
  };

  getPickerApiKey = async () => {
    try {
      const { key } = await this.q(SecretService.getSecretKey({ key: 'pickerApiKey' }));
      return key;
    } catch (e) {
      this.handleError(e);
    }
  };

  /**        LOCATIONS      */

  getLocationsByProjectId = async (projectId: string): Promise<Location[] | undefined> => {
    try {
      const { locations } = await this.q(LocationService.listLocations(projectId));
      return locations;
    } catch (e) {
      this.handleError(e);
    }
  };

  createLocation = async (location: TCreateLocationRequest) => {
    try {
      this.setIsSaving(true);
      const { location: createdLocation } = await this.q(LocationService.createLocation(location));
      return createdLocation;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  deleteLocation = async (locationId: string) => {
    try {
      this.setIsSaving(true);
      await this.q(LocationService.deleteLocation(locationId));
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };

  updateLocation = async (updatedLocation: Location) => {
    const request: TUpdateLocationRequest = {
      ...updatedLocation,
      sessionActivityIds: updatedLocation.session_activities?.map((sa) => sa.id),
    };
    try {
      this.setIsSaving(true);
      const { location } = await this.q(LocationService.updateLocation(updatedLocation.id, request));
      return location;
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setIsSaving(false);
    }
  };
}
